/* eslint-disable prettier/prettier */
import React, { useEffect, useState, useMemo } from 'react';
import './ChatTranscript.scss';
import { useAxios } from 'hooks/useAxios';
import { getDataFromSingleEndpoint } from 'services/Torogoz/TogorozApi';
import { useStore as useGlobalStore } from 'store/useGlobalStore';
import { getFullFormat } from 'helpers/dateFormatters';
import { RiSparkling2Fill, RiMessage3Line } from 'react-icons/ri';
import CategoryList from './CategoryList';
import BreadCrumb from 'components/Breadcrumb';
import SentimentFace from './SentimentFace';
import AccordionSection from './AccordionSection/AccordionSection';
import AiAnalysis from './AiAnalysis/AiAnalysis';
import { toDisplaySentiment } from 'utils/sentiment';
import { transformRowData, createFieldMap } from 'helpers/dataGridTransformation';
import ChatTranscriptSkeleton from './ChatTranscriptSkeleton';

interface MessageCategory {
  id: string;
  name?: string;
  sentiment?: string;
  parentId?: string;
}

interface Message {
  id?: string;
  sentimentCategory?: string;
  emitterRole?: string;
  chatFields?: {
    msgTime?: string;
  };
  verbatim?: string;
  categories?: MessageCategory[];
}

interface SessionData {
  date: string;
  summary?: string;
  transcript?: Message[];
}

interface ChatTranscriptProps {
  setOpenTranscript: (value: boolean) => void;
  context?: any;
  data: any;
  columns?: any[];
}

type SentimentOption = 'all' | 'positive' | 'negative';

const normalizeFilterValue = (value?: string): SentimentOption => {
  const normalized = (value ?? '').trim().toLowerCase();

  const valid: Record<string, SentimentOption> = {
    positive: 'positive',
    negative: 'negative',
  };

  return valid[normalized] ?? 'all';
};

const ChatTranscript: React.FC<ChatTranscriptProps> = ({
  setOpenTranscript,
  data,
  context,
  columns,
}) => {
  const { endpoints, dashboardConfig, dateFilter } = useGlobalStore((state) => ({
    endpoints: state.endpoints,
    dashboardConfig: state.configDashboard,
    dateFilter: state.dateFilter,
  }));

  // Transform session data using column mapping
  const transformedData = useMemo(() => {
    if (!data || !columns) return null;
    const fieldMap = createFieldMap(columns);
    return transformRowData(data, fieldMap);
  }, [data, columns]);

  const [sessionData, setSessionData] = useState<SessionData | null>(null);

  const [systemMessages, setSystemMessages] = useState<Message[]>([]);
  const [chatMessages, setChatMessages] = useState<Message[]>([]);

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [activeAccordion, setActiveAccordion] = useState<'messages' | 'summary'>('messages');
  const [sentimentFilter, setSentimentFilter] = useState<SentimentOption>('all');

  const { response: sessions, axiosFetch, loading } = useAxios();

  const categoryList = useMemo(() => {
    if (!chatMessages.length) return null;
    const categories = chatMessages.reduce<MessageCategory[]>((acc, message) => {
      if (message.categories && message.categories.length > 0) {
        message.categories.forEach((category) => {
          if (category.name && !acc.find((cat) => cat.id === category.id)) {
            acc.push(category);
          }
        });
      }
      return acc;
    }, []);

    if (categories.length === 0) {
      return null;
    }
    return categories.filter((cat): cat is Required<MessageCategory> => !!cat.name);
  }, [chatMessages]);

  useEffect(() => {
    setSelectedCategoryId(context?.l2categoryguid || null);
  }, [context]);

  useEffect(() => {
    // For external data, use transformed sessionid; for internal, use data.id
    const sessionId = dashboardConfig.external ? transformedData?.SessionId : data?.id;
    if (!sessionId) return;
    const getData = async () => {
      await getDataFromSingleEndpoint(
        endpoints.getWidgetData,
        {
          filters: {
            session_id: sessionId,
            date_interval: dateFilter,
          },
          source: 'chat',
          actionName: 'session-transcript',
          ...(dashboardConfig.external
            ? {
                external: true,
              }
            : {
                external: false,
                mockEndpoint: 'data-session-transcript',
              }),
        },
        axiosFetch
      );
    };
    getData();
  }, [data, transformedData]);

  useEffect(() => {
    if (!sessions) return;
    setSessionData(sessions.data);
  }, [sessions]);

  useEffect(() => {
    if (!sessions) return;
    const transcript = Array.isArray(sessions.data?.transcript) ? sessions.data.transcript : [];

    const systemMessages = transcript.filter(
      (message: Message) => message.emitterRole === 'System'
    );
    const chatMessages = transcript.filter((message: Message) => message.emitterRole !== 'System');
    setSystemMessages(systemMessages);
    setChatMessages(chatMessages);
  }, [sessions]);

  const filteredChatMessages = useMemo(() => {
    if (!chatMessages) return null;
    if (sentimentFilter === 'all') return chatMessages;

    const returnedMessages = chatMessages.filter(
      (message: Message) => normalizeFilterValue(message.sentimentCategory) === sentimentFilter
    );

    return returnedMessages;
  }, [chatMessages, sentimentFilter]);

  if (loading || !sessionData) {
    return <ChatTranscriptSkeleton />;
  }

  return (
    <div className="chat-transcript">
      <div className="chat-transcript_ghost-section"></div>
      <div className="chat-transcript_header-section">
        <div className="chat-transcript_controls">
          <div className="chat-transcript_info">
            <span className="transcript-title">Session Transcript</span>
            <span className="transcript-date">Date: {getFullFormat(sessionData.date)}</span>
          </div>
          <BreadCrumb
            context={{ categoryName: 'Session Transcript', subCategoryName: sentimentFilter }}
            onRequestClose={() => setOpenTranscript(false)}
            hideClose
            placeholderLabel="Filter by Sentiment"
            isPadding={false}
            hideLabel
            items={[
              { value: 'all', label: 'All' },
              { value: 'positive', label: 'Positive' },
              { value: 'negative', label: 'Negative' },
            ]}
            selectedValue={sentimentFilter === 'all' ? undefined : sentimentFilter}
            onChangeItem={(val: string) => setSentimentFilter(normalizeFilterValue(val))}
          />
        </div>
        {categoryList && (
          <CategoryList
            categories={categoryList}
            onSelect={setSelectedCategoryId}
            selectedCategoryId={selectedCategoryId}
          />
        )}
      </div>
      <div className="chat-transcript_body">
        <AccordionSection
          title="Messages"
          icon={<RiMessage3Line />}
          isExpanded={activeAccordion === 'messages'}
          onToggle={() => setActiveAccordion('messages')}
          className="messages-accordion"
          fullHeight={true}
        >
          <div className="chat-transcript_scroll">
            <div className="chat-transcript_system-messages">
              {systemMessages?.map((message: any, index: number) => {
                return (
                  <div
                    key={message.id ?? index}
                    className={`chat-transcript_system-message ${index === 0 ? 'ct_sm_first' : ''}`}
                  >
                    <p>{message.verbatim ?? ''}</p>
                    <span className="ct_sm_time">{message.chatFields?.msgTime ?? '--'}</span>
                  </div>
                );
              })}
            </div>

            {filteredChatMessages?.map((message: Message, index: number) => {
              const messageHasSelectedCategory = message.categories?.some(
                (cat) => cat.id === selectedCategoryId
              );
              const isAgent = message.emitterRole === 'Agent';
              const displaySentiment = toDisplaySentiment(message.sentimentCategory);
              return (
                <div className={`chat-transcript_message-container`} key={message.id ?? index}>
                  <div className={`chat-transcript_message ${isAgent ? 'is-agent' : ''}`}>
                    <span className={`message-transcript_thumbnail ${isAgent ? 'is-agent' : ''}`}>
                      {message.emitterRole === 'Agent' ? 'A' : 'C'}
                    </span>
                    <div className="message-transcript_content">
                      <div
                        className={`message-transcript-bubble ${isAgent ? 'is-agent' : ''} ${
                          messageHasSelectedCategory ? 'highlight-category' : ''
                        }`}
                      >
                        <p className="message-bubble_verbatim">{message.verbatim ?? ''}</p>
                      </div>
                      <span className={`message-transcript-timestamp ${isAgent ? 'is-agent' : ''}`}>
                        {message.chatFields?.msgTime ?? '--'}
                      </span>
                    </div>
                    {!isAgent && displaySentiment !== 'Neutral' && displaySentiment !== null ? (
                      <SentimentFace sentiment={displaySentiment} size={25} />
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </AccordionSection>

        <AccordionSection
          title="AI Analysis"
          icon={<RiSparkling2Fill />}
          isExpanded={activeAccordion === 'summary'}
          onToggle={() => setActiveAccordion('summary')}
          className="summary-accordion"
          fullHeight={true}
        >
          <AiAnalysis
            sessionId={dashboardConfig.external ? transformedData?.SessionId : data?.id}
            isExternal={dashboardConfig.external}
          />
        </AccordionSection>
      </div>
    </div>
  );
};

export default ChatTranscript;
